<script setup lang="ts">
import { computed, nextTick, ref, useId, watch, type ComponentPublicInstance } from 'vue'

import { useReportFilters } from '../../composables/useReportFilters'
import type { ReportColumn } from '../../models/column.types'
import {
  MATCH_MODES,
  type FilterDropdownables,
  type FilterMatchMode,
  type ReportFilter,
  type SavedReportFilterInput,
  type SavedReportFilterOption,
} from '../../models/filter.types'
import { normalizeReportFilterInput } from '../../service/reportFilterInput'

import FilterPopup from './FilterPopup.vue'
import ReportFilterChips from './ReportFilterChips.vue'
import ReportFilterPicker from './ReportFilterPicker.vue'

import { useI18n } from '@/shared/i18n'
import { DropdownPanel, Icon } from '@/shared/ui'

const props = withDefaults(
  defineProps<{
    modelValue?: readonly ReportFilter[]
    filters?: readonly ReportFilter[]
    query?: string | null
    columns: readonly ReportColumn[]
    savedFilters?: readonly SavedReportFilterOption[]
    dropdownables?: FilterDropdownables
    matchModes?: readonly FilterMatchMode[]
    variant?: 'default' | 'standalone'
    label?: string
    placeholder?: string
    flexCol?: boolean
    compactHeight?: boolean
    disabled?: boolean
    numericMode?: FilterMatchMode
    checkChange?: boolean
    isExport?: boolean
    onLoadDropdownValues?: ((field: string) => Promise<unknown> | unknown) | undefined
  }>(),
  {
    modelValue: () => [],
    savedFilters: () => [],
    dropdownables: () => ({}),
    matchModes: () => MATCH_MODES,
    variant: 'default',
    flexCol: false,
    compactHeight: false,
    disabled: false,
    numericMode: 'greater_than',
    checkChange: false,
    isExport: false,
  },
)
const emit = defineEmits<{
  'update:modelValue': [filters: ReportFilter[]]
  'update:filters': [filters: ReportFilter[]]
  'update:query': [query: string]
  updateFilters: [filters: ReportFilter[]]
  apply: [filters: ReportFilter[]]
  save: [saved: SavedReportFilterInput]
  remove: [id: string]
}>()

const { t } = useI18n()
const inputId = useId()
const panelId = `${inputId}-panel`
const filterState = useReportFilters(() => props.columns, {
  initialFilters: () => props.filters ?? props.modelValue,
  initialQuery: () => props.query ?? '',
  dropdownables: () => props.dropdownables,
})
const queryState = filterState.query
const menuOpen = ref(false)
const selectedColumn = ref<ReportColumn | null>(null)
const savedFilterIds = ref<Array<string | undefined>>([])
const reference = ref<HTMLElement | null>(null)
const editorPopup = ref<ComponentPublicInstance | null>(null)
interface PendingSavedBinding {
  index: number
  previousIds: Set<string>
  name: string
  filter: ReportFilter
}
const pendingSavedBindings: PendingSavedBinding[] = []

const currentFilters = computed<readonly ReportFilter[]>(() => filterState.filters.value)
const effectiveDropdownables = filterState.dropdownables
const rootClasses = computed(() =>
  props.variant === 'standalone'
    ? [
        'flex flex-wrap items-center rounded-lg gap-2 bg-canvas p-1 px-2',
        `focus:outline-info focus:outline-[2px] focus:-outline-offset-2 outline-1 -outline-offset-1 outline-border-strong rounded-sm relative w-full bg-surface ${props.flexCol ? 'h-12' : 'min-h-12'} text-left text-fg`,
        props.disabled ? 'cursor-default pointer-events-none opacity-80' : 'cursor-pointer',
      ]
    : [
        props.flexCol
          ? 'flex-col bg-surface min-h-10 outline outline-border-strong'
          : 'flex-wrap items-center bg-surface-sunken min-h-11 border border-border-strong',
        'flex rounded-sm gap-2 py-2 px-3 flex-auto',
      ],
)

watch(
  () => props.query,
  (value) => {
    if (value !== undefined && (value ?? '') !== queryState.value) filterState.setQuery(value ?? '')
  },
)
watch(
  () => props.filters ?? props.modelValue,
  (value) => {
    const previous = currentFilters.value
    savedFilterIds.value = value.map((filter, index) => {
      const savedId = savedFilterIds.value[index]
      const previousFilter = previous[index]
      return savedId && previousFilter && sameFilter(previousFilter, filter) ? savedId : undefined
    })
    filterState.setFilters(value)
  },
  { deep: true },
)
watch(
  () => props.savedFilters,
  () => bindPendingSavedFilter(),
  { deep: true },
)

function setQuery(value: string): void {
  filterState.setQuery(value)
  emit('update:query', value)
}

function cloneFilter(filter: ReportFilter): ReportFilter {
  return { ...filter, value: Array.isArray(filter.value) ? [...filter.value] : filter.value }
}

function emitModels(filters: ReportFilter[]): void {
  const value = filters.map(cloneFilter)
  filterState.setFilters(value)
  emit('update:modelValue', value)
  emit('update:filters', value)
  emit('updateFilters', value)
}

function commit(filters: ReportFilter[]): void {
  emitModels(filters)
  emit('apply', filters.map(cloneFilter))
  setQuery('')
}

function appendSaved(saved: SavedReportFilterOption): void {
  savedFilterIds.value = [...savedFilterIds.value, saved.id]
  commit([
    ...currentFilters.value.map(cloneFilter),
    normalizeReportFilterInput(saved.filter, props.columns),
  ])
  closeMenu()
}

function appendFilter(filter: ReportFilter): void {
  savedFilterIds.value = [...savedFilterIds.value, undefined]
  commit([...currentFilters.value.map(cloneFilter), cloneFilter(filter)])
  closeMenu()
}

function requestSave(index: number, saved: SavedReportFilterInput): void {
  if (saved.id) {
    savedFilterIds.value[index] = saved.id
    removePendingBindingsAt(index)
  } else {
    pendingSavedBindings.push({
      index,
      previousIds: new Set(props.savedFilters.map((option) => option.id)),
      name: saved.name.trim(),
      filter: cloneFilter(saved.filter),
    })
  }
  emit('save', saved)
}

function bindPendingSavedFilter(): void {
  for (let index = pendingSavedBindings.length - 1; index >= 0; index -= 1) {
    const pending = pendingSavedBindings[index]
    if (!pending) continue
    const active = currentFilters.value[pending.index]
    if (!active || !sameFilter(active, pending.filter)) continue
    const candidates = props.savedFilters.filter(
      (saved) =>
        !pending.previousIds.has(saved.id) &&
        saved.name.trim() === pending.name &&
        sameFilter(saved.filter, pending.filter),
    )
    if (candidates.length !== 1) continue
    savedFilterIds.value[pending.index] = candidates[0]?.id
    pendingSavedBindings.splice(index, 1)
  }
}

function removePendingBindingsAt(filterIndex: number): void {
  for (let index = pendingSavedBindings.length - 1; index >= 0; index -= 1) {
    if (pendingSavedBindings[index]?.index === filterIndex) pendingSavedBindings.splice(index, 1)
  }
}

function updateFilter(index: number, filter: ReportFilter): void {
  commit(
    currentFilters.value.map((item, itemIndex) => cloneFilter(itemIndex === index ? filter : item)),
  )
}

function removeFilter(index: number): void {
  if (props.disabled) return
  removePendingBindingsAt(index)
  for (const pending of pendingSavedBindings) {
    if (pending.index > index) pending.index -= 1
  }
  savedFilterIds.value = savedFilterIds.value.filter((_id, itemIndex) => itemIndex !== index)
  commit(currentFilters.value.filter((_filter, itemIndex) => itemIndex !== index).map(cloneFilter))
}

function removeSaved(id: string): void {
  emit('remove', id)
}

function sameFilter(left: ReportFilter, right: ReportFilter): boolean {
  return (
    left.field === right.field &&
    left.match_mode === right.match_mode &&
    left.is_change === right.is_change &&
    left.direction === right.direction &&
    left.sub_type === right.sub_type &&
    JSON.stringify(left.value) === JSON.stringify(right.value)
  )
}

function openMenu(): void {
  if (props.disabled) return
  selectedColumn.value = null
  menuOpen.value = true
}

async function selectColumn(column: ReportColumn): Promise<void> {
  selectedColumn.value = column
  await nextTick()
  const root = editorPopup.value?.$el
  if (!(root instanceof HTMLElement)) return
  root
    .querySelector<HTMLElement>(
      'input:not([disabled]), button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )
    ?.focus({ preventScroll: true })
}

function closeMenu(): void {
  menuOpen.value = false
  selectedColumn.value = null
}
</script>

<template>
  <section
    data-pdf-filter-root
    :class="[rootClasses, 'max-md:h-auto max-md:min-h-11']"
    :style="props.compactHeight ? { height: '2.75rem', minHeight: '2.75rem' } : undefined"
    :aria-label="t('reports.filters.title')"
  >
    <label
      v-if="variant === 'standalone' && label"
      class="report-filters__label absolute -top-[0.5rem] left-[0.5rem] z-raised rounded-sm bg-[linear-gradient(to_bottom,transparent_40%,var(--ui-color-surface)_50%)] px-1 text-[0.75rem] font-medium text-muted"
      >{{ label }}</label
    >
    <ReportFilterChips
      v-if="currentFilters.length && !(variant === 'standalone' && flexCol)"
      :filters="currentFilters"
      :columns="columns"
      :saved-filters="savedFilters"
      :saved-filter-ids="savedFilterIds"
      :dropdownables="effectiveDropdownables"
      :match-modes="matchModes"
      :variant="variant"
      :disabled="disabled"
      :numeric-mode="numericMode"
      :check-change="checkChange"
      :is-export="isExport"
      :on-load-dropdown-values="onLoadDropdownValues"
      @apply="updateFilter"
      @save="requestSave"
      @remove="removeFilter"
      @remove-saved="removeSaved"
    />

    <div
      ref="reference"
      data-pdf-filter-input-wrap
      class="report-filters__input-wrap relative flex h-7 w-max flex-1 items-center"
    >
      <label
        :for="inputId"
        class="report-filters__input-label flex h-7 flex-auto items-center gap-xs"
        @click="openMenu"
      >
        <Icon v-if="variant === 'standalone'" name="filter" :size="14" />
        <input
          :id="inputId"
          data-pdf-filter-input
          :value="queryState"
          type="text"
          :placeholder="
            placeholder ??
            (variant === 'standalone'
              ? t('reports.filters.standalonePlaceholder')
              : t('reports.filters.searchPlaceholder'))
          "
          class="report-filters__input h-7 w-[15rem] rounded-md border-0 bg-transparent p-0 text-[0.75rem] text-fg placeholder:text-muted outline-none max-md:w-full max-md:min-w-[10rem]"
          autocomplete="off"
          :disabled="disabled"
          role="combobox"
          :aria-label="t('reports.filters.title')"
          aria-haspopup="dialog"
          aria-autocomplete="list"
          :aria-expanded="menuOpen"
          :aria-controls="panelId"
          @keydown.down.prevent="openMenu"
          @input="setQuery(($event.target as HTMLInputElement).value)"
        />
      </label>
    </div>
  </section>

  <div
    v-if="variant === 'standalone' && flexCol && currentFilters.length"
    data-report-filter-external-chips
    class="flex flex-wrap items-center gap-2"
  >
    <ReportFilterChips
      external
      :filters="currentFilters"
      :columns="columns"
      :saved-filters="savedFilters"
      :saved-filter-ids="savedFilterIds"
      :dropdownables="effectiveDropdownables"
      :match-modes="matchModes"
      :variant="variant"
      :disabled="disabled"
      :numeric-mode="numericMode"
      :check-change="checkChange"
      :is-export="isExport"
      :on-load-dropdown-values="onLoadDropdownValues"
      @apply="updateFilter"
      @save="requestSave"
      @remove="removeFilter"
      @remove-saved="removeSaved"
    />
  </div>

  <DropdownPanel
    :id="panelId"
    v-model:open="menuOpen"
    :reference="reference"
    placement="bottom-start"
    :offset="4"
    class="report-filters__floating max-h-[var(--ui-floating-available-height)]"
    :class="{ 'is-editor': selectedColumn, 'w-max': selectedColumn, 'w-[15rem]': !selectedColumn }"
    role="dialog"
    :aria-label="t('reports.filters.title')"
    @close="closeMenu"
  >
    <FilterPopup
      v-if="selectedColumn"
      ref="editorPopup"
      :columns="columns"
      :column="selectedColumn"
      :dropdownables="effectiveDropdownables"
      :match-modes="matchModes"
      :numeric-mode="numericMode"
      :check-change="checkChange"
      :is-export="isExport"
      :on-load-dropdown-values="onLoadDropdownValues"
      @apply="appendFilter"
      @save="requestSave(currentFilters.length, $event)"
      @close="closeMenu"
    />
    <ReportFilterPicker
      v-else
      :columns="columns"
      :saved-filters="savedFilters"
      :query="queryState"
      @select-saved="appendSaved"
      @remove-saved="removeSaved"
      @select-column="selectColumn"
    />
  </DropdownPanel>
</template>
